import { getDashboardContacts } from "../../api/axios";
import { useState, useEffect, useMemo } from "react";
import { nf } from "../../utilities/Utils";
import useAuth from "../../hooks/useAuth";
import LoadingContainer from "../../components/LoadingContainer";
import {
  DashboardBars,
  DashboardMetric,
  DashboardPanel,
  DashboardSplitBars,
  getCountValue,
  getSourceLabel,
  sortByValue,
} from "./DashboardVisuals";

const DashboardPetOwnersTable = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [petOwnersSummary, setPetOwnersSummary] = useState({ total: 0, dogs: 0, cats: 0, both: 0 });
  const [dogAgeSummary, setDogAgeSummary] = useState([]);
  const [catAgeSummary, setCatAgeSummary] = useState([]);
  const [dogSizeSummary, setDogSizeSummary] = useState([]);
  const [contactsByState, setContactsByState] = useState([]);
  const [contactsBySource, setContactsBySource] = useState([]);
  const [contactsByActivations, setContactsByActivations] = useState([]);
  const [contactsByEvents, setContactsByEvents] = useState([]);
  const { auth } = useAuth();

  const paramsKey = useMemo(() => JSON.stringify(props?.params ?? {}), [props?.params]);
  const dashboardParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const ownersParams = { ...dashboardParams, idContactType: "1" };
        const [
          totalOwners,
          dogOwnersRes,
          catOwnersRes,
          dogCatOwnersRes,
          dogAgeRes,
          catAgeRes,
          dogSizeRes,
          stateRes,
          sourceRes,
          activationRes,
          eventRes,
        ] = await Promise.all([
          getDashboardContacts({ auth, params: { ...ownersParams } }),
          getDashboardContacts({ auth, params: { ...ownersParams, hasDog: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, hasCat: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, hasDog: true, hasCat: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, byDogAge: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, byCatAge: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, byDogSize: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, byState: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, idSource: 0, bySource: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, idSource: 11, bySource: true } }),
          getDashboardContacts({ auth, params: { ...ownersParams, idSource: 6, bySource: true } }),
        ]);

        setPetOwnersSummary({
          total: totalOwners?.count ?? 0,
          dogs: dogOwnersRes?.count ?? 0,
          cats: catOwnersRes?.count ?? 0,
          both: dogCatOwnersRes?.count ?? 0,
        });

        setDogAgeSummary(sortByValue(Array.isArray(dogAgeRes?.results) ? dogAgeRes.results : []));
        setCatAgeSummary(sortByValue(Array.isArray(catAgeRes?.results) ? catAgeRes.results : []));
        setDogSizeSummary(sortByValue(Array.isArray(dogSizeRes?.results) ? dogSizeRes.results : []));
        setContactsByState(sortByValue(Array.isArray(stateRes?.results) ? stateRes.results : [], ["dog_count", "cat_count"]));
        setContactsBySource(Array.isArray(sourceRes?.results) ? sourceRes.results : []);
        setContactsByActivations(Array.isArray(activationRes?.results) ? activationRes.results : []);
        setContactsByEvents(Array.isArray(eventRes?.results) ? eventRes.results : []);
        setError(null);
      } catch (error) {
        setError(error);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [auth, dashboardParams]);

  if (error) {
    return (
      <div className="dashboard-overview dashboard-detail">
        <div className="dashboard-empty">No se pudieron cargar los indicadores de dueños.</div>
      </div>
    );
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className="dashboard-overview dashboard-detail">
        <section className="dashboard-hero">
          <div className="dashboard-hero__intro">
            <span className="dashboard-eyebrow">Dueños de mascota</span>
            <h2 className="dashboard-hero__title">Perfil de la audiencia pet owner</h2>
            <p className="dashboard-hero__copy">
              Distribución por especie, etapa de vida, talla y origen de captación.
            </p>
          </div>
          <div className="dashboard-hero__metrics">
            <DashboardMetric label="Total dueños" value={petOwnersSummary.total} detail="contactos tipo dueño" accent="red" />
            <DashboardMetric label="Con perros" value={petOwnersSummary.dogs} detail="tenencia canina" accent="gold" />
            <DashboardMetric label="Con gatos" value={petOwnersSummary.cats} detail="tenencia felina" accent="ink" />
            <DashboardMetric label="Ambas especies" value={petOwnersSummary.both} detail="perro y gato" accent="sand" />
          </div>
        </section>

        <section className="dashboard-grid dashboard-detail-grid">
          <DashboardPanel kicker="Origen" title="Fuentes" className="dashboard-panel--feature">
            <DashboardSplitBars items={contactsBySource} getLabel={getSourceLabel} limit={10} />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Activaciones">
            <DashboardSplitBars items={contactsByActivations} getLabel={getSourceLabel} limit={10} />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Eventos">
            <DashboardSplitBars items={contactsByEvents} getLabel={getSourceLabel} limit={10} />
          </DashboardPanel>

          <DashboardPanel kicker="Perros" title="Etapas de vida">
            <DashboardBars
              items={dogAgeSummary}
              getLabel={(item) => item.label ?? "Sin etapa"}
              getValue={getCountValue}
              tone="gold"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Gatos" title="Etapas de vida">
            <DashboardBars
              items={catAgeSummary}
              getLabel={(item) => item.label ?? "Sin etapa"}
              getValue={getCountValue}
              tone="ink"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Perros" title="Talla">
            <DashboardBars
              items={dogSizeSummary}
              getLabel={(item) => item.label ?? "Sin talla"}
              getValue={getCountValue}
              tone="sand"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Geografía" title="Distribución por provincia" className="dashboard-panel--feature">
            <DashboardSplitBars
              items={contactsByState}
              getLabel={(item) => item.state_name ?? "Sin provincia"}
              limit={12}
            />
          </DashboardPanel>

          <DashboardPanel kicker="Síntesis" title="Lectura operativa">
            <div className="dashboard-scoreboard">
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Dueños</span>
                <strong className="dashboard-scoreboard__value">{nf(petOwnersSummary.total)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Perros</span>
                <strong className="dashboard-scoreboard__value">{nf(petOwnersSummary.dogs)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Gatos</span>
                <strong className="dashboard-scoreboard__value">{nf(petOwnersSummary.cats)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Provincias</span>
                <strong className="dashboard-scoreboard__value">{contactsByState.length}</strong>
              </div>
            </div>
          </DashboardPanel>
        </section>
      </div>
    </LoadingContainer>
  );
};

export default DashboardPetOwnersTable;
